import {AvatarRef, Avatar as EzsAvatar, ConversationState} from '@ezs/avatar';
import {filter, findIndex, isEmpty, map, random, trim} from 'lodash';
import React, {useMemo, useContext, useCallback, useRef, useEffect, useState} from 'react';

import {DataWrapper, Icon, Loader, ConfirmInteractionModal} from 'components';
import {AVATAR_BG_PATH} from 'constants/images';
import {ClientContext} from 'context';
import {useClientSession, usePrevious} from 'hooks';
import {IntermediateMessage, SessionActivityTextChatData} from 'interfaces';
import {DefaultParticipant, InteractiveChatAction, TextChatContentType, TextChatMessage} from 'types';

type TTSState = 'loading' | 'success' | 'error' | 'user_interaction_required';

const DEFAULT_AVATAR_HEIGHT = 461;

type Props = {
    host: string;
    url: string;
    avatarZoom?: number;
    backgroundZoom?: number;
    backgroundImageUrl?: string;
    recentClientMessage?: TextChatMessage;
    intermediateMessages?: IntermediateMessage[];
    message?: TextChatMessage;
    onSpeakStarted?: (content: string, meta?: any) => void;
    onSpeakFinished?: (content: string, meta?: any) => void;
    onLoadCompleted?: () => void;
}

export const Avatar: React.FC<Props> = ({
    host, url, avatarZoom: configuredAvatarZoom, backgroundZoom: configuredBackgroundZoom, backgroundImageUrl,
    recentClientMessage, intermediateMessages,
    message: recentChatbotMessage, onLoadCompleted, onSpeakFinished, onSpeakStarted
}) => {
    const [avatarState, setAvatarState] = useState<TTSState>();
    const [showInteractionModal, setShowInteractionModal] = useState(false);
    const prevClientMessage = usePrevious(recentClientMessage);
    const [lastSpokenMessageId, setLastSpokenMessageId] = useState<string | null>(null);

    const avatarRef = useRef<AvatarRef>();
    const speechRequestIdRef = useRef(0);
    const avatarContainerRef = useRef<HTMLDivElement>();

    const [initialized, setInitialized] = useState(false);
    const [avatarHeight, setAvatarHeight] = useState<number>();

    const [activeIntermediateMessage, setActiveIntermediateMessage] = useState<{id: string, text?: string}>(null);

    const {onSubmitMessage} = useContext(ClientContext);
    const {avatarActivity} = useClientSession();
    const conversationState = useMemo(() => {
        const data = avatarActivity?.data as SessionActivityTextChatData;
        return data?.state?.[DefaultParticipant.Chatbot]?.typing ? ConversationState.Thinking : ConversationState.Listening;
    }, [avatarActivity]);

    const stateIcon: React.ReactElement = useMemo(() => {
        if (avatarState === 'loading') {
            return <Loader />;
        } else if (avatarState === 'error') {
            return (<Icon name='alert-triangle' color='alert' />);
        } else if (avatarState === 'user_interaction_required') {
            return (<Icon name='alert-triangle' color='warning' />);
        } else {
            return null;
        }
    }, [avatarState]);

    const handleLoadCompleted = useCallback(() => {
        setInitialized(true);
        onLoadCompleted?.();

        if (avatarRef.current) {
            if (!isEmpty(intermediateMessages)) {
                try {
                    avatarRef.current.preload(map(intermediateMessages, (phrase) => ({text: phrase.text})));
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.warn('[Avatar] Preload text failed', error);
                }
            }
        }
    }, [intermediateMessages, onLoadCompleted]);

    const getIntermediateMessage = useCallback((message: TextChatMessage) => {
        if (message.contentType === TextChatContentType.IdleReminder) {
            return null;
        }
        const clientMessageContent = message.content;
        if (!clientMessageContent) {
            return null;
        }

        const possibleMessages = filter(intermediateMessages, (phrase) => {
            if (phrase.conditions.lengthMore && phrase.conditions.lengthMore < clientMessageContent.length) {
                return true;
            }
            if (phrase.conditions.includesText && phrase.conditions.includesText.some((text) => clientMessageContent.includes(text))) {
                return true;
            }
            if (phrase.conditions.includesPattern && clientMessageContent.match(phrase.conditions.includesPattern)) {
                return true;
            }
            return false;
        });

        if (isEmpty(possibleMessages)) {
            return null;
        }

        return possibleMessages[random(possibleMessages.length - 1)];
    }, [intermediateMessages]);

    const avatarZoom = useMemo(() => {
        if (configuredAvatarZoom !== undefined) {
            return configuredAvatarZoom;
        }
        if (avatarHeight > 1200) {
            return 440;
        }
        if (avatarHeight > 800) {
            return 320;
        }
        if (avatarHeight > 600) {
            return 200;
        }
        if (avatarHeight > 300) {
            return 150;
        } else {
            return avatarHeight / 2.5;
        }
    }, [avatarHeight, configuredAvatarZoom]);

    const backgroundZoom = useMemo(() => {
        if (configuredBackgroundZoom !== undefined) {
            return configuredBackgroundZoom;
        }
        if (avatarHeight > 800) {
            return 250;
        }
        if (avatarHeight > 300) {
            return 100;
        } else {
            return avatarHeight / 2.5;
        }
    }, [avatarHeight, configuredBackgroundZoom]);

    const avatarStyles: React.CSSProperties = useMemo(() => {
        const common: React.CSSProperties = {
            position: 'relative',
            height: `${avatarHeight}px`,
        };

        return {
            ...common,
            maxWidth: '100%'
        };
    }, [avatarHeight]);

    const handleOptionSelect = useCallback(async (index: number) => {
        const selectedOption = recentChatbotMessage?.data?.options?.[index];

        if (selectedOption) {
            await onSubmitMessage({
                content: trim(selectedOption.content),
                contentType: selectedOption.contentType,
                data: {
                    chatOptionIndex: index,
                    nodeValue: {
                        ...selectedOption.nodeValue,
                        action: selectedOption.nodeValue?.action === InteractiveChatAction.CallAgentConfirm ?
                            InteractiveChatAction.CallAgent : selectedOption.nodeValue?.action
                    }
                }
            });
        }
    }, [recentChatbotMessage, onSubmitMessage]);

    const handleResize = useCallback(() => {
        setTimeout(() => {
            const parent = avatarContainerRef?.current?.parentElement;
            const rect = parent?.getBoundingClientRect();
            setAvatarHeight(rect.height || DEFAULT_AVATAR_HEIGHT);
        }, 0);
    }, []);

    const handleSpeakFinished = useCallback((text: string, meta: any) => {
        onSpeakFinished?.(text, meta);
        setActiveIntermediateMessage(null);

        const optionIndex = findIndex(
            recentChatbotMessage?.data?.options, (o) => o?.nodeValue?.action === InteractiveChatAction.CallAgentConfirm
        );

        if (optionIndex > -1) {
            handleOptionSelect(optionIndex);
        }
    }, [onSpeakFinished, recentChatbotMessage?.data?.options, handleOptionSelect]);

    const handleSpeakStarted = useCallback((text: string, meta: any) => {
        onSpeakStarted?.(text, meta);
    }, [onSpeakStarted]);

    // Reusable function to attempt speaking with proper error handling
    const attemptSpeak = useCallback(async (message: TextChatMessage) => {
        if (!avatarRef.current || !message?.content) {
            return {success: false, error: 'No avatar or message'};
        }

        try {
            // eslint-disable-next-line no-console
            console.debug('[Avatar] Attempting to speak:', message.content.substring(0, 50) + '...');

            const result = await avatarRef.current.speak(message.content, {
                id: message.id, isIntermediateMessage: message.meta?.isIntermediateMessage
            });

            // Handle different response statuses
            if (result?.status === 'cancelled') {
                // Speech was cancelled (e.g., interrupted by a new message)
                return {success: false, cancelled: true};
            } else if (result?.status === 'user_interaction_required') {
                return {success: false, requiresInteraction: true};
            } else if (result?.status === 'error') {
                return {success: false, error: result.error || 'Speak failed'};
            } else if (result?.status === 'ended' || result?.status === 'success' || !result?.status) {
                return {success: true};
            } else {
                return {success: false, error: 'Unknown status'};
            }
        } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error('[Avatar] Speak error:', error);
            return {success: false, error: error?.message || 'Speak exception'};
        }
    }, []);

    const speakWithFallback = useCallback(async (message: TextChatMessage) => {
        const requestId = ++speechRequestIdRef.current;
        setAvatarState('loading');
        const speakResult = await attemptSpeak(message);
        if (requestId !== speechRequestIdRef.current) {
            return;
        }

        if (speakResult.success) {
            setAvatarState('success');
        } else if (speakResult.cancelled) {
            setAvatarState('success');
            // Speech was cancelled - not an error, just interrupted by a new message
            // eslint-disable-next-line no-console
            console.debug('[Avatar] Speech cancelled (interrupted)');
        } else if (speakResult.requiresInteraction) {
            // eslint-disable-next-line no-console
            console.warn('[Avatar] User interaction required for audio playback');
            setAvatarState('user_interaction_required');
            setShowInteractionModal(true);
        } else {
            setAvatarState('error');
            // eslint-disable-next-line no-console
            console.error('[Avatar] Speak failed:', speakResult.error);
        }
    }, [attemptSpeak]);

    const handleInteractionConfirm = useCallback(async () => {
        setShowInteractionModal(false);

        if (!avatarRef.current) {
            return;
        }

        if (!recentChatbotMessage) {
            return;
        }

        // Retry speaking using the reusable function
        const requestId = ++speechRequestIdRef.current;
        setAvatarState('loading');
        const speakResult = await attemptSpeak(recentChatbotMessage);
        if (requestId !== speechRequestIdRef.current) {
            return;
        }

        if (speakResult.success) {
            setAvatarState('success');
        } else if (speakResult.cancelled) {
            setAvatarState('success');
            // Speech was cancelled - this is not an error, just interrupted
            // eslint-disable-next-line no-console
            console.debug('[Avatar] Speech cancelled (interrupted by new message)');
        } else if (speakResult.requiresInteraction) {
            // This shouldn't happen after we just initialized
            setAvatarState('user_interaction_required');
            // eslint-disable-next-line no-console
            console.warn('[Avatar] Still requires interaction after init');
        } else {
            setAvatarState('error');
            // eslint-disable-next-line no-console
            console.error('[Avatar] Speak failed after init:', speakResult.error);
        }
    }, [recentChatbotMessage, attemptSpeak]);

    useEffect(() => {
        if (initialized && recentClientMessage?.id && prevClientMessage?.id !== recentClientMessage.id) {
            if (avatarRef.current?.isBusy()) {
                avatarRef.current.stop();
            }

            const intermediateMessage = getIntermediateMessage(recentClientMessage);
            if (!intermediateMessage) {
                return;
            }
            setActiveIntermediateMessage({
                id: `intermediate-${Date.now()}`,
                text: intermediateMessage?.text,
            });
        }
    }, [initialized, recentClientMessage, getIntermediateMessage, prevClientMessage?.id]);

    useEffect(() => {
        if (activeIntermediateMessage?.id) {
            speakWithFallback({
                content: activeIntermediateMessage.text,
                id: activeIntermediateMessage.id,
                date: new Date().toISOString(),
                index: 0,
                contentType: TextChatContentType.Chatbot,
                meta: {
                    isIntermediateMessage: true
                }
            });
        }
    }, [activeIntermediateMessage?.id, activeIntermediateMessage?.text, speakWithFallback]);

    // Effect to handle chatbot messages and trigger avatar speech
    useEffect(() => {
        if (!initialized || !avatarRef.current) {
            return;
        }

        if (activeIntermediateMessage?.id || !recentChatbotMessage?.content || recentChatbotMessage?.id === lastSpokenMessageId) {
            return;
        }

        setLastSpokenMessageId(recentChatbotMessage?.id);
        speakWithFallback(recentChatbotMessage);

    }, [initialized, recentChatbotMessage, activeIntermediateMessage?.id, speakWithFallback, lastSpokenMessageId]);

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    return (
        <div ref={avatarContainerRef} style={avatarStyles}>
            {!!stateIcon &&
                <div className='tw-absolute tw-right-0 tw-h-6 tw-w-6'>
                    {stateIcon}
                </div>}

            <DataWrapper
                isError={!host || !url}
                isLoading={!initialized && !avatarState && !!host && !!url}
                hideContentOnLoading={false}
                errorClassName='tw-px-8 tw-py-2 tw-text-center tw-text-opacity-100 tw-bg-default'
            >
                <EzsAvatar
                    ref={avatarRef}
                    host={host}
                    avatarUrl={url}
                    conversationState={conversationState}
                    showBackground
                    backgroundColor='#ffffff'
                    showLoader={false}
                    style={{height: avatarStyles.height}}
                    className='full'
                    cameraProps={{zoom: avatarZoom}}
                    backgroundZoom={backgroundZoom}
                    // metrics
                    // debug
                    backgroundImageUrl={backgroundImageUrl || AVATAR_BG_PATH}
                    onSpeakStarted={handleSpeakStarted}
                    onSpeakFinished={handleSpeakFinished}
                    onLoadCompleted={handleLoadCompleted}
                />
            </DataWrapper>

            <ConfirmInteractionModal
                open={showInteractionModal}
                onClose={handleInteractionConfirm}
            />
        </div>
    );
};